import { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  Flex,
  Text,
  Select,
  Input,
  Badge,
  Avatar,
  HStack,
  VStack,
  Spinner,
  useToast,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { FaSearch, FaFileExcel, FaDesktop, FaHistory } from "react-icons/fa";
import { DateTime } from "luxon";
import { getUserStatistics } from "../../../services/historyService";
import { HistoryViewModel } from "./approveModal";
import MultipleSelect from "../../../components/MultipleSelect";

interface UserAlterationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialDate?: string;
  finalDate?: string;
  companyId?: string;
}

// Fora do componente: antes era recriado a cada chamada, ou seja, um objeto novo
// por célula renderizada.
const PROPERTY_DISPLAY_NAMES: Record<string, string> = {
  cvfRef: "CVF Ref",
  clientRef: "Ref. Cliente",
  status: "Status",
  arrivalDate: "Data de Chegada",
  departurePort: "Porto de Saída",
  dischargePort: "Porto de Descarga",
  exporter: "Exportador",
  ncm: "NCM",
  // Add more mappings as needed
};

const getPropertyDisplayName = (property: string) =>
  PROPERTY_DISPLAY_NAMES[property] || property;

const UserAlterationsModal = ({
  isOpen,
  onClose,
  initialDate,
  finalDate,
  companyId,
}: UserAlterationsModalProps) => {
  const [alterations, setAlterations] = useState<HistoryViewModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedProperty, setSelectedProperty] = useState<string>("");
  const toast = useToast();
  const requestIdRef = useRef(0);

  useEffect(() => {
    // O componente fica montado junto da página de processos, então ao fechar
    // é preciso soltar o dataset (pode ter milhares de registros) — senão ele
    // fica retido em memória pelo resto da vida da página.
    if (!isOpen) {
      setAlterations([]);
      setSelectedUsers([]);
      setSearchTerm("");
      setSelectedProperty("");
      return;
    }

    // Descarta respostas obsoletas: trocar de data/empresa (ou reabrir) com uma
    // requisição em voo fazia todas resolverem e chamarem setState.
    const requestId = ++requestIdRef.current;
    let active = true;

    const fetchAlterations = async () => {
      setLoading(true);
      try {
        const data = await getUserStatistics({
          initialDate,
          finalDate,
          companyId,
        });

        if (!active || requestId !== requestIdRef.current) return;

        const list: HistoryViewModel[] = data ?? [];
        setAlterations(list);
        setSelectedUsers(
          Array.from(new Set(list.map((a) => a.changedBy?.name ?? "Excel")))
        );
      } catch (error) {
        if (!active) return;
        console.log("asd-error", error);
        toast({
          title: "Erro ao carregar alterações",
          status: "error",
          duration: 3000,
        });
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchAlterations();

    return () => {
      active = false;
    };
  }, [isOpen, initialDate, finalDate, companyId]);

  // Derivados via useMemo em vez de state: antes cada um mantinha uma cópia
  // extra do dataset e disparava um render adicional a cada tecla digitada.
  const users = useMemo(
    () =>
      Array.from(new Set(alterations.map((a) => a.changedBy?.name ?? "Excel"))),
    [alterations]
  );

  const properties = useMemo(
    () => Array.from(new Set(alterations.map((a) => a.property))).sort(),
    [alterations]
  );

  const userStats = useMemo(() => {
    const stats = new Map<string, number>();
    alterations.forEach((alt) => {
      const name = alt.changedBy?.name ?? "Excel";
      stats.set(name, (stats.get(name) ?? 0) + 1);
    });
    return Array.from(stats, ([name, count]) => ({ name, count })).sort(
      (a, b) => b.count - a.count
    );
  }, [alterations]);

  const filteredAlterations = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const userSet = new Set(selectedUsers);

    const filtered = alterations.filter((a) => {
      if (userSet.size > 0 && !userSet.has(a.changedBy?.name ?? "Excel"))
        return false;
      if (selectedProperty && a.property !== selectedProperty) return false;
      if (term && !a.property.toLowerCase().includes(term)) return false;
      return true;
    });

    // Timestamp pré-calculado (decorate-sort-undecorate): ordenar chamando
    // DateTime.fromISO dentro do comparador custava 2 parses do luxon por
    // comparação, ou seja O(n log n) parses sobre o dataset inteiro.
    return filtered
      .map((a) => ({ item: a, at: Date.parse(a.changedAt) }))
      .sort((x, y) => y.at - x.at)
      .map((x) => x.item);
  }, [alterations, selectedUsers, searchTerm, selectedProperty]);

  const clearFilters = () => {
    setSelectedUsers([]);
    setSearchTerm("");
    setSelectedProperty("");
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: filteredAlterations.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 53,
    overscan: 12,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - virtualRows[virtualRows.length - 1].end
      : 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay backdropFilter="blur(2px)" bg="blackAlpha.600" />
      <ModalContent maxH="90vh" borderRadius="16px" boxShadow="0 12px 32px rgba(16,24,40,0.18)">
        <ModalHeader borderBottom="1px solid #E2E8F0">
          <HStack>
            <FaHistory />
            <Text>Histórico de Alterações</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {loading ? (
            <Flex justify="center" align="center" h="200px">
              <Spinner size="xl" color="var(--icon-color)" />
            </Flex>
          ) : (
            <>
              {/* Summary Stats */}
              <Box
                mb="24px"
                p="16px"
                backgroundColor="gray.50"
                borderRadius="8px"
              >
                <Text fontWeight="bold" mb="12px">
                  Resumo por Usuário
                </Text>
                <Flex flexWrap="wrap" gap="12px">
                  {userStats.map((stats) => (
                    <Box
                      key={stats.name}
                      p="12px"
                      backgroundColor="white"
                      borderRadius="8px"
                      boxShadow="sm"
                      minW="200px"
                    >
                      <HStack mb="8px">
                        {/* <Avatar size="sm" name={stats.name} /> */}
                        <VStack align="start" spacing="0">
                          <Text fontWeight="semibold" fontSize="sm">
                            {stats.name}
                          </Text>
                          <Text fontSize="xs" color="gray.600">
                            {stats.count} alterações
                          </Text>
                        </VStack>
                      </HStack>
                      <HStack spacing="8px" fontSize="xs">
                        {stats.name === "Excel" ? (
                          <Badge colorScheme="green">
                            <HStack spacing="4px">
                              <FaFileExcel />
                              <Text>{stats.count}</Text>
                            </HStack>
                          </Badge>
                        ) : (
                          <Badge colorScheme="blue">
                            <HStack spacing="4px">
                              <FaDesktop />
                              <Text>{stats.count}</Text>
                            </HStack>
                          </Badge>
                        )}
                      </HStack>
                    </Box>
                  ))}
                </Flex>
              </Box>

              {/* Filters */}
              <Flex gap="12px" mb="24px" flexWrap="wrap">
                <InputGroup maxW="300px">
                  <InputLeftElement pointerEvents="none">
                    <FaSearch color="gray" />
                  </InputLeftElement>
                  <Input
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    backgroundColor="white"
                  />
                </InputGroup>
                <MultipleSelect
                  selectedValues={selectedUsers}
                  onChange={(values) => setSelectedUsers(values)}
                  placeholder="Todos os usuários"
                  options={users}
                  maxWidth="200px"
                  maxHeight="80px"
                />

                <Select
                  placeholder="Todas as propriedades"
                  value={selectedProperty}
                  onChange={(e) => setSelectedProperty(e.target.value)}
                  backgroundColor="white"
                  maxW="200px"
                >
                  {properties.map((prop) => (
                    <option key={prop} value={prop}>
                      {getPropertyDisplayName(prop)}
                    </option>
                  ))}
                </Select>

                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearFilters}
                  colorScheme="gray"
                >
                  Limpar filtros
                </Button>
              </Flex>

              {/* Results Count */}
              <Text mb="12px" fontSize="sm" color="gray.600">
                Mostrando {filteredAlterations.length} de {alterations.length}{" "}
                alterações
              </Text>

              {/* Table */}
              <Box ref={scrollRef} overflowX="auto" maxH="400px" overflowY="auto">
                <Table variant="simple" size="sm">
                  <Thead
                    position="sticky"
                    top="0"
                    backgroundColor="white"
                    zIndex="1"
                  >
                    <Tr>
                      <Th>Data/Hora</Th>
                      <Th>Usuário</Th>
                      <Th>Campo</Th>
                      <Th>Valor Anterior</Th>
                      <Th>Valor Novo</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paddingTop > 0 && (
                      <Tr>
                        <Td
                          colSpan={5}
                          style={{ height: paddingTop, padding: 0, border: 0 }}
                        />
                      </Tr>
                    )}
                    {virtualRows.map((virtualRow) => {
                      const alt = filteredAlterations[virtualRow.index];
                      return (
                      <Tr key={alt.id} data-index={virtualRow.index} ref={rowVirtualizer.measureElement} _hover={{ backgroundColor: "gray.50" }}>
                        <Td>
                          <VStack align="start" spacing="0">
                            <Text fontSize="xs">
                              {DateTime.fromISO(alt.changedAt).toFormat(
                                "dd/MM/yyyy"
                              )}
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              {DateTime.fromISO(alt.changedAt).toFormat(
                                "HH:mm:ss"
                              )}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <HStack>
                            <Avatar
                              size="xs"
                              name={alt.changedBy?.name ?? "Excel"}
                            />
                            <Text fontSize="sm">
                              {alt.changedBy?.name ?? "Excel"}
                            </Text>
                          </HStack>
                        </Td>
                        <Td>
                          <Text fontSize="sm">
                            {getPropertyDisplayName(alt.property)}
                          </Text>
                        </Td>
                        <Td>
                          <Text
                            fontSize="sm"
                            color="red.600"
                            maxW="150px"
                            isTruncated
                            title={alt.before ?? ""}
                          >
                            {alt.before || "-"}
                          </Text>
                        </Td>
                        <Td>
                          <Text
                            fontSize="sm"
                            color="green.600"
                            maxW="150px"
                            isTruncated
                            title={alt.after ?? ""}
                          >
                            {alt.after || "-"}
                          </Text>
                        </Td>
                      </Tr>
                      );
                    })}
                    {paddingBottom > 0 && (
                      <Tr>
                        <Td
                          colSpan={5}
                          style={{ height: paddingBottom, padding: 0, border: 0 }}
                        />
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>

              {filteredAlterations.length === 0 && (
                <Flex justify="center" align="center" h="100px">
                  <Text color="gray.500">Nenhuma alteração encontrada</Text>
                </Flex>
              )}
            </>
          )}
        </ModalBody>

        <ModalFooter borderTop="1px solid #E2E8F0">
          <Button variant="outline" borderColor="#E2E8F0" borderRadius="10px" onClick={onClose}>Fechar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UserAlterationsModal;
